'use client';

import React, { useRef, useEffect, useState, useMemo } from 'react';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { usePreloadImages } from 'hooks/dom/usePreloadImages';
import { Heading, Body2 } from './styleConstants';
import { ContainerLG } from 'components/shared/Container';
import { drawImageSafe } from 'utils/drawImageSafe';
import styles from './IntelligentInsights.module.css';

const INSIGHT_CARDS = [
  {
    id: 'sleep',
    title: 'SLEEP',
    subtitle:
      "Discover the key to sleeping better. Get an everyday sleep score that shows how well you've rested.",
    startPercent: 0,
    endPercent: 22,
  },
  {
    id: 'recovery',
    title: 'RECOVERY',
    subtitle:
      "Understand how sleep, stress rhythm, skin temperature, and HRV affect your body's restoration. Improve your recovery with actionable insights.",
    startPercent: 23,
    endPercent: 38,
  },
  {
    id: 'movement',
    title: 'MOVEMENT',
    subtitle:
      'Continuously monitor your physical activity. Track all movement, from light activities to intense workouts, providing a comprehensive view of your daily activity.',
    startPercent: 39,
    endPercent: 54,
  },
  {
    id: 'stress',
    title: 'STRESS',
    subtitle:
      "Decode if you're stimulated, relaxed, or stressed by analyzing HR, RHR, and HRV trends throughout your circadian rhythm.",
    startPercent: 55,
    endPercent: 70,
  },
  {
    id: 'cycle',
    title: 'CYCLE & OVULATION',
    subtitle:
      'Track your unique cycle pattern. Personalized health insights help reveal irregularities, fertile windows, phases, and more to proactively manage your reproductive health.',
    startPercent: 71,
    endPercent: 88,
  },
  {
    id: 'powerplugs',
    title: 'POWERPLUGS',
    subtitle:
      'PowerPlugs are precision micro-tools built for targeted insights. Whether your goal is to optimize athletic performance or stay proactive with your health, PowerPlugs offer you a more personalized experience on your Ultrahuman App.',
    startPercent: 89,
    endPercent: 100,
  },
];

const SEQUENCE_DIR = '/web_v2/uh-x-diesel/insights/display-tone-mapped-og';
const START_FRAME = 1;
const END_FRAME = 452;
const TOTAL_FRAMES = END_FRAME - START_FRAME + 1;

const frameUrl = (frameIndex: number) => {
  return getCompressedAssetUrl(
    `${SEQUENCE_DIR}/frame_${String(frameIndex).padStart(4, '0')}.png`,
  );
};

interface DisplaySequenceRef {
  updateFrame: (progress: number) => void;
}

const DisplaySequence = React.memo(
  React.forwardRef<DisplaySequenceRef, { onReady: () => void }>(
    ({ onReady }, ref) => {
      const canvasRef = useRef<HTMLCanvasElement | null>(null);
      const imagesRef = useRef<HTMLImageElement[]>([]);
      const renderState = useRef({ frame: 0 });
      const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
      const lastDrawnFrameRef = useRef<number>(-1);
      const [isReady, setIsReady] = useState(false);
      const canvasDimensionsRef = useRef({ width: 0, height: 0 });
      const drawParamsRef = useRef({
        drawX: 0,
        drawY: 0,
        drawWidth: 0,
        drawHeight: 0,
      });

      // Generate all frame URLs
      const frameUrls = useMemo(() => {
        return Array.from({ length: TOTAL_FRAMES }, (_, i) =>
          frameUrl(START_FRAME + i),
        );
      }, []);

      // Use the preload hook to start loading images
      usePreloadImages(frameUrls);

      // Track when images are ready (they're already being loaded by usePreloadImages)
      useEffect(() => {
        let isCancelled = false;
        const loaded: HTMLImageElement[] = new Array(TOTAL_FRAMES);
        let loadedCount = 0;

        const checkAllLoaded = () => {
          if (loadedCount === TOTAL_FRAMES && !isCancelled) {
            imagesRef.current = loaded;
            setIsReady(true);
            onReady();
          }
        };

        // Create Image objects - browser cache will handle reuse from usePreloadImages
        frameUrls.forEach((url, index) => {
          const img = new Image();
          img.src = url;
          img.onload = img.onerror = () => {
            if (!isCancelled) {
              loaded[index] = img;
              loadedCount++;
              checkAllLoaded();
            }
          };
        });

        return () => {
          isCancelled = true;
        };
      }, [frameUrls, onReady]);

      const drawFrame = (frameIdx: number) => {
        const ctx = ctxRef.current;
        const canvas = canvasRef.current;
        if (!ctx || !canvas) return;

        const roundedIdx = Math.round(frameIdx);
        const clampedIdx = Math.max(0, Math.min(TOTAL_FRAMES - 1, roundedIdx));

        // Skip if same frame as last drawn
        if (lastDrawnFrameRef.current === clampedIdx) return;

        const img = imagesRef.current[clampedIdx];
        if (!img || !img.complete || img.naturalWidth === 0) return;

        // Ensure alpha is reset to full opacity for vibrant colors
        ctx.globalAlpha = 1.0;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const { drawX, drawY, drawWidth, drawHeight } = drawParamsRef.current;
        if (!drawImageSafe(ctx, img, drawX, drawY, drawWidth, drawHeight))
          return;
        lastDrawnFrameRef.current = clampedIdx;
      };

      const calculateDrawParams = () => {
        const displayWidth = canvasDimensionsRef.current.width;
        const displayHeight = canvasDimensionsRef.current.height;

        if (imagesRef.current.length === 0) return;
        const img = imagesRef.current[0];
        if (!img || img.naturalWidth === 0) return;

        const imgAspect = img.naturalWidth / img.naturalHeight;
        const canvasAspect = displayWidth / displayHeight;

        let drawWidth: number;
        let drawHeight: number;
        let drawX: number;
        let drawY: number;

        if (imgAspect > canvasAspect) {
          drawHeight = displayHeight;
          drawWidth = drawHeight * imgAspect;
          drawX = (displayWidth - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = displayWidth;
          drawHeight = drawWidth / imgAspect;
          drawX = 0;
          drawY = (displayHeight - drawHeight) / 2;
        }

        drawParamsRef.current = { drawX, drawY, drawWidth, drawHeight };
      };

      useIsomorphicLayoutEffect(() => {
        if (!isReady || !canvasRef.current) return;
        const canvas = canvasRef.current;

        // Get and cache context once
        const ctx = canvas.getContext('2d', {
          desynchronized: true,
          alpha: false,
        });
        if (!ctx) return;

        // Configure canvas for optimal image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.globalAlpha = 1.0;

        ctxRef.current = ctx;

        const updateCanvasSize = () => {
          const rect = canvas.getBoundingClientRect();
          canvasDimensionsRef.current = {
            width: rect.width,
            height: rect.height,
          };
          const dpr = window.devicePixelRatio || 1;
          canvas.width = rect.width * dpr;
          canvas.height = rect.height * dpr;
          ctx.scale(dpr, dpr);
          calculateDrawParams();
          drawFrame(renderState.current.frame);
        };
        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);
        drawFrame(0);
        return () => {
          window.removeEventListener('resize', updateCanvasSize);
        };
      }, [isReady]);

      useEffect(() => {
        if (!isReady) return;
        renderState.current.frame = 0;
        drawFrame(0);
      }, [isReady]);

      // Expose frame update function via ref
      useIsomorphicLayoutEffect(() => {
        if (!isReady) return;
        const updateFrame = (progress: number) => {
          const targetFrame = progress * (TOTAL_FRAMES - 1);
          renderState.current.frame = targetFrame;
          requestAnimationFrame(() => {
            drawFrame(targetFrame);
          });
        };

        if (typeof ref === 'function') {
          ref({ updateFrame });
        } else if (ref) {
          ref.current = { updateFrame };
        }
      }, [isReady, ref]);

      return (
        <canvas
          className={styles.sequenceCanvas}
          ref={canvasRef}
          role="img"
          aria-label="Intelligent insights display sequence"
        />
      );
    },
  ),
);

DisplaySequence.displayName = 'DisplaySequence';

const IntelligentInsights: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const stickyWrapperRef = useRef<HTMLDivElement>(null);
  const sequenceWrapperRef = useRef<HTMLDivElement>(null);
  const sequenceRef = useRef<DisplaySequenceRef | null>(null);
  const cardsTrackRef = useRef<HTMLDivElement>(null);
  const cardsViewportRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const gsapContext = useGsapContext();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [sequenceReady, setSequenceReady] = useState(false);

  useIsomorphicLayoutEffect(() => {
    if (
      !stickyWrapperRef.current ||
      !sequenceWrapperRef.current ||
      !cardsTrackRef.current ||
      !sequenceReady
    )
      return;

    const cardsTrack = cardsTrackRef.current;
    const cards = cardRefs.current.filter(Boolean) as HTMLDivElement[];

    const getCardPositions = () => {
      const positions: number[] = [];
      let currentY = 0;
      const trackGap = cardsTrackRef.current
        ? parseInt(getComputedStyle(cardsTrackRef.current).gap || '12', 10)
        : 12;
      cards.forEach((card, index) => {
        positions[index] = currentY;
        currentY += card.offsetHeight + trackGap;
      });
      return positions;
    };

    gsap.set(cards, { opacity: 0.3 });
    if (cards[0]) {
      gsap.set(cards[0], { opacity: 1 });
    }

    const updateCardsOnScroll = (progress: number) => {
      const progressPercent = progress * 100;
      const positions = getCardPositions();

      let activeIndex = 0;
      for (let i = 0; i < INSIGHT_CARDS.length; i++) {
        const card = INSIGHT_CARDS[i];
        if (
          progressPercent >= card.startPercent &&
          progressPercent < card.endPercent
        ) {
          activeIndex = i;
          break;
        }
        if (progressPercent >= card.endPercent) {
          activeIndex = Math.min(i + 1, INSIGHT_CARDS.length - 1);
        }
      }

      const currentCard = INSIGHT_CARDS[activeIndex];
      const cardProgress =
        (progressPercent - currentCard.startPercent) /
        (currentCard.endPercent - currentCard.startPercent);
      const clampedProgress = Math.max(0, Math.min(1, cardProgress));

      const viewportHeight =
        cardsViewportRef.current?.offsetHeight || (isMobile ? 240 : 400);
      const cardHeight = cards[activeIndex]?.offsetHeight || 180;
      const centerOffset = (viewportHeight - cardHeight) / 2;

      let targetY = -positions[activeIndex] + centerOffset;

      const transitionStart = 0.9;
      if (
        activeIndex < INSIGHT_CARDS.length - 1 &&
        clampedProgress > transitionStart
      ) {
        const nextCardY = -positions[activeIndex + 1] + centerOffset;
        const transitionProgress =
          (clampedProgress - transitionStart) / (1 - transitionStart);
        targetY = targetY + (nextCardY - targetY) * transitionProgress;
      }

      gsap.to(cardsTrack, {
        y: targetY,
        duration: 0.3,
        ease: 'power2.out',
        overwrite: true,
      });

      cards.forEach((card, index) => {
        const distance = Math.abs(index - activeIndex);
        let targetOpacity = 0.3;

        if (index === activeIndex) {
          targetOpacity = 1;
        } else if (distance === 1) {
          targetOpacity = 0.4;
        }

        gsap.to(card, {
          opacity: targetOpacity,
          duration: 0.3,
          ease: 'power2.out',
          overwrite: true,
        });
      });
    };

    gsapContext.add(() => {
      ScrollTrigger.create({
        trigger: stickyWrapperRef.current,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.1,
        onUpdate: (self) => {
          if (sequenceRef.current) {
            sequenceRef.current.updateFrame(self.progress);
          }

          updateCardsOnScroll(self.progress);
        },
      });
    });
  }, [gsapContext, isMobile, sequenceReady]);

  return (
    <section
      className={styles.section}
      ref={sectionRef}
      aria-label="Intelligent Insights"
    >
      <div className={styles.stickyWrapper} ref={stickyWrapperRef}>
        <div className={styles.stickyContainer}>
          <Heading className={styles.mainTitle} $color="d_red">
            Intelligent
            <br />
            Insights
          </Heading>
          <ContainerLG className={styles.phoneContainer}>
            <div className={styles.frameWrapper}>
              <div className={styles.framePositioner}>
                <div
                  className={styles.sequenceWrapper}
                  ref={sequenceWrapperRef}
                >
                  <DisplaySequence
                    ref={sequenceRef}
                    onReady={() => setSequenceReady(true)}
                  />
                </div>
                <img
                  className={styles.desktopFrameImage}
                  src={getCompressedAssetUrl(
                    '/web_v2/uh-x-diesel/insights/desktop-phone-frame.png',
                  )}
                  alt=""
                  loading="lazy"
                  aria-hidden="true"
                />
                <img
                  className={styles.mobileFrameImage}
                  src={getCompressedAssetUrl(
                    '/web_v2/uh-x-diesel/insights/mobile-phone-frame.png',
                  )}
                  alt=""
                  loading="lazy"
                  aria-hidden="true"
                />
              </div>
            </div>
          </ContainerLG>
          <div
            className={styles.cardsViewport}
            ref={cardsViewportRef}
            aria-live="polite"
          >
            <div className={styles.cardsTrack} ref={cardsTrackRef}>
              {INSIGHT_CARDS.map((card, index) => (
                <div
                  className={styles.insightCard}
                  key={card.id}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                  }}
                >
                  <Body2 className={styles.cardTitle} $color="white" $uppercase>
                    {card.title}
                  </Body2>
                  <Body2 className={styles.cardSubtitle} $color="white">
                    {card.subtitle}
                  </Body2>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntelligentInsights;
